/**
 * Channel-agnostic readers for non-EPA history entries.
 *
 * Fax entries store fax-named fields (`faxed_at`, `poc`, `fax_required`) while
 * call entries keep the call-named ones (`called_at`, `calling_poc`,
 * `call_required`). Entries written before the fax tab used call names for both
 * channels, so every reader falls back to them.
 */

import { CallingHistoryLog } from "./callingHistoryService";

/**
 * POC who handled the outreach.
 *
 * @input entry - History log entry
 * @return Firebase UID of the POC, or an empty string when unset
 */
export const getEntryPoc = (entry: CallingHistoryLog): string =>
  entry.poc ?? entry.calling_poc ?? "";

/**
 * Reason the order was routed to non-EPA outreach.
 *
 * @input entry - History log entry
 * @return Reason text, or an empty string when unset
 */
export const getEntryReason = (entry: CallingHistoryLog): string =>
  entry.non_epa_reason ?? entry.calling_reason ?? "";

/**
 * Whether outreach was required on the order when the summary was saved.
 *
 * @input entry - History log entry
 * @return yes / no / archive, or an empty string when unset
 */
export const getEntryRequired = (entry: CallingHistoryLog): string =>
  entry.fax_required ?? entry.call_required ?? "";

/**
 * Full display timestamp captured when the summary was recorded.
 *
 * @input entry - History log entry
 * @return Timestamp string, e.g. `07/28/2026 05:06 EST`, or an empty string
 */
export const getEntryLoggedAt = (entry: CallingHistoryLog): string =>
  entry.faxed_at ?? entry.called_at ?? "";

/**
 * Date the call was made or the fax was sent.
 *
 * @input entry - History log entry
 * @return Date string in MM/DD/YYYY, or an empty string when unset
 */
export const getEntryLoggedDate = (entry: CallingHistoryLog): string =>
  entry.faxed_date ?? entry.called_date ?? "";
